import React from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import type { Align, Side } from "@design-system/overlay/overlayPositioning";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import {
    type OptionsWithRequireDescription,
    useKeyboardCommand,
} from "@hooks/keyboard/useKeyboardCommand";
import type { ButtonProps } from "./Button";

type HotkeyButtonElement = React.ReactElement<
    ButtonProps & React.RefAttributes<HTMLButtonElement>
>;

/**
 * Inline mode hands the hint back to the caller instead of injecting it into
 * the child's `rightIcon`. Placing it is then checked by Button's own union,
 * which forbids icon slots on `iconOnly` buttons; an injected slot would
 * have silently bypassed that.
 */
type InlineHotkeyChildren = (
    hotkeyHint: React.ReactNode,
) => HotkeyButtonElement;

interface WithHotkeyBaseProps {
    hotKey: string;
    hotKeyOptions: OptionsWithRequireDescription;
    tooltip?: React.ReactNode;
    tooltipPlacement?: Side;
    tooltipAlign?: Align;
}

export type WithHotkeyProps =
    | (WithHotkeyBaseProps & {
          hotKeyDisplayPosition: "inline";
          children: InlineHotkeyChildren;
      })
    | (WithHotkeyBaseProps & {
          hotKeyDisplayPosition?: "none" | "tooltip";
          children: HotkeyButtonElement;
      });

/**
 * Registers a hotkey that clicks the button it wraps, and shows the key hint
 * either inline in the button or in its tooltip.
 */
export const WithHotkey = ({
    hotKey,
    hotKeyOptions,
    hotKeyDisplayPosition = "tooltip",
    tooltip,
    tooltipPlacement,
    tooltipAlign,
    children,
}: WithHotkeyProps) => {
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const child =
        typeof children === "function"
            ? children(
                  <span data-slot-box="">
                      <Shortcut keys={hotKey} />
                  </span>,
              )
            : children;
    const isDisabled = child.props.disabled;

    useKeyboardCommand({
        keys: hotKey,
        callback: () => buttonRef.current?.click(),
        options: {
            ...hotKeyOptions,
            enabled: Boolean(hotKeyOptions.enabled) || !isDisabled,
        },
    });

    // LegacyRef narrows out string refs, which the design system never uses.
    const childRef = child.props.ref;
    const button = React.cloneElement(child, {
        ref: mergeRefs([
            buttonRef,
            typeof childRef === "string" ? undefined : childRef,
        ]),
    });
    if (hotKeyDisplayPosition === "tooltip") {
        return (
            <Tooltip
                content={tooltip}
                shortcut={hotKey}
                side={tooltipPlacement}
                align={tooltipAlign}
            >
                {button}
            </Tooltip>
        );
    }
    return (
        <Tooltip content={tooltip} side={tooltipPlacement} align={tooltipAlign}>
            {button}
        </Tooltip>
    );
};
